import React, { useState, useEffect, useMemo } from 'react'
import { Search, Receipt, Loader2 } from 'lucide-react'
import { posApiService } from '@/services/posApiService'
import { useSettingsStore } from '@/store/settingsStore'
import { ReceiptModal } from '@/components/pos/ReceiptModal'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import type { Transaction } from '@/types/pos'

type DateFilter = 'all' | 'today' | '7d' | '30d'

function formatDate(value: Date | string | undefined): string {
  if (!value) return '—'
  const d = new Date(value)
  return d.toLocaleDateString(undefined, { dateStyle: 'short' }) + ' ' + d.toLocaleTimeString(undefined, { timeStyle: 'short' })
}

function toDateStart(d: Date): Date {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}

function toDateEnd(d: Date): Date {
  const x = new Date(d)
  x.setHours(23, 59, 59, 999)
  return x
}

export const TransactionHistoryPage: React.FC = () => {
  const settings = useSettingsStore((s) => s.settings)
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [dateFilter, setDateFilter] = useState<DateFilter>('all')
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null)
  const [loadingReceipt, setLoadingReceipt] = useState(false)

  useEffect(() => {
    let cancelled = false
    const fetchList = async () => {
      setLoading(true)
      try {
        const res = await posApiService.getTransactions()
        if (!cancelled && res.success && Array.isArray(res.data)) {
          setTransactions(res.data)
        }
      } catch (e) {
        if (!cancelled) setTransactions([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    fetchList()
    return () => { cancelled = true }
  }, [])

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    const now = new Date()
    let list = transactions

    if (dateFilter === 'today') {
      const start = toDateStart(now)
      const end = toDateEnd(now)
      list = list.filter((t) => {
        const created = new Date(t.createdAt ?? 0)
        return created >= start && created <= end
      })
    } else if (dateFilter === '7d') {
      const start = toDateStart(new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000))
      list = list.filter((t) => new Date(t.createdAt ?? 0) >= start)
    } else if (dateFilter === '30d') {
      const start = toDateStart(new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000))
      list = list.filter((t) => new Date(t.createdAt ?? 0) >= start)
    }

    if (q) {
      list = list.filter(
        (t) =>
          (t.receiptNumber ?? t.transactionNumber ?? t.id ?? '').toLowerCase().includes(q) ||
          (t.customerName ?? '').toLowerCase().includes(q)
      )
    }

    return list.sort((a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime())
  }, [transactions, searchQuery, dateFilter])

  const totalAmount = useMemo(() => filtered.reduce((sum, t) => sum + (t.total ?? 0), 0), [filtered])

  const handleViewReceipt = async (t: Transaction) => {
    setSelectedTransaction(null)
    setLoadingReceipt(true)
    try {
      const res = await posApiService.getTransaction(t.id)
      if (res.success && res.data) {
        setSelectedTransaction(res.data)
      } else {
        setSelectedTransaction(t)
      }
    } catch {
      setSelectedTransaction(t)
    } finally {
      setLoadingReceipt(false)
    }
  }

  const itemCount = (t: Transaction) => (Array.isArray(t.items) ? t.items.length : 0)

  return (
    <div className="w-full">
      <div className="mb-6">
        <h1 className="text-3xl font-bold esales-text font-display">Transaction History</h1>
        <p className="text-muted-foreground mt-1">View past sales and receipts</p>
      </div>

      <div className="glass-card p-6">
        {/* Search and filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by receipt # or customer..."
              className="w-full pl-10 pr-4 py-2.5 bg-background border border-border rounded-xl text-sm placeholder:text-muted-foreground focus:ring-2 focus:ring-primary focus:outline-none"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {(['all', 'today', '7d', '30d'] as const).map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setDateFilter(key)}
                className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                  dateFilter === key
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                }`}
              >
                {key === 'all' ? 'All' : key === 'today' ? 'Today' : key === '7d' ? 'Last 7 days' : 'Last 30 days'}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4 mb-4 text-sm text-muted-foreground">
          <span>{filtered.length} transactions</span>
          <span className="font-semibold text-foreground">Total: Rs.{totalAmount.toFixed(2)}</span>
        </div>

        {loading ? (
          <div className="py-16 flex justify-center">
            <LoadingSpinner size="md" message="Loading transactions..." />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-muted-foreground">
            <Receipt className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No transactions found</p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto rounded-xl border border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/30 border-b border-border">
                    <th className="text-left p-3 font-semibold">Receipt #</th>
                    <th className="text-left p-3 font-semibold">Date</th>
                    <th className="text-left p-3 font-semibold">Customer</th>
                    <th className="text-left p-3 font-semibold">Items</th>
                    <th className="text-left p-3 font-semibold">Payment</th>
                    <th className="text-right p-3 font-semibold">Total</th>
                    <th className="text-left p-3 font-semibold">Status</th>
                    <th className="w-24 p-3" />
                  </tr>
                </thead>
                <tbody>
                  {filtered.slice(0, 200).map((t) => (
                    <tr
                      key={t.id}
                      className="border-b border-border/50 hover:bg-muted/20 transition-colors"
                    >
                      <td className="p-3 font-mono">{t.receiptNumber ?? t.transactionNumber ?? t.id ?? '—'}</td>
                      <td className="p-3">{formatDate(t.createdAt)}</td>
                      <td className="p-3">{t.customerName ?? 'Walk-in'}</td>
                      <td className="p-3">{itemCount(t)}</td>
                      <td className="p-3 capitalize">{(t.paymentMethod ?? 'cash').replace('_', ' ')}</td>
                      <td className="p-3 text-right font-semibold">Rs.{(t.total ?? 0).toFixed(2)}</td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          (t.status === 'completed' && 'bg-green-500/20 text-green-500') ||
                          (t.status === 'refunded' && 'bg-amber-500/20 text-amber-500') ||
                          (t.status === 'partial_refund' && 'bg-yellow-500/20 text-yellow-500') ||
                          (t.status === 'voided' && 'bg-red-500/20 text-red-500') ||
                          'bg-muted text-muted-foreground'
                        }`}>
                          {t.status === 'partial_refund' ? 'Partial Refund' : (t.status ?? 'completed')}
                        </span>
                      </td>
                      <td className="p-3">
                        <button
                          type="button"
                          onClick={() => handleViewReceipt(t)}
                          disabled={loadingReceipt}
                          className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 text-xs font-semibold disabled:opacity-50"
                        >
                          {loadingReceipt ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Receipt className="w-3.5 h-3.5" />}
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-3">
              {filtered.slice(0, 100).map((t) => (
                <div
                  key={t.id}
                  className="p-4 rounded-xl border border-border bg-background/50"
                >
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <p className="font-mono font-semibold">{t.receiptNumber ?? t.transactionNumber ?? t.id ?? '—'}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{formatDate(t.createdAt)}</p>
                      <p className="text-sm mt-1">{t.customerName ?? 'Walk-in'}</p>
                    </div>
                    <p className="font-bold text-primary">Rs.{(t.total ?? 0).toFixed(2)}</p>
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/50">
                    <span className="text-xs text-muted-foreground">{itemCount(t)} items · {(t.paymentMethod ?? 'cash').replace('_', ' ')}</span>
                    <button
                      type="button"
                      onClick={() => handleViewReceipt(t)}
                      disabled={loadingReceipt}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 text-xs font-semibold"
                    >
                      <Receipt className="w-3.5 h-3.5" /> View receipt
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {filtered.length > 200 && (
              <p className="text-xs text-muted-foreground mt-4 text-center">Showing latest 200. Use search or filters to narrow.</p>
            )}
          </>
        )}
      </div>

      {selectedTransaction && (
        <ReceiptModal
          transaction={selectedTransaction}
          storeName={settings.receiptHeader || settings.storeName}
          storeAddress={settings.storeAddress}
          storePhone={settings.storePhone}
          storeEmail={settings.storeEmail}
          receiptFooter={settings.receiptFooter}
          onClose={() => setSelectedTransaction(null)}
          onNewTransaction={undefined}
        />
      )}
    </div>
  )
}
